import { beginTask, endTask } from 'redux-nprogress';
import { groupBy } from 'lodash';
import { selectQuery } from '../../../../store/reducers/location.selectors';
import { selectPositions } from '../../../../store/reducers/positions';
import { loadWeeks, selectCurrent, selectWeeks } from '../../../../store/reducers/season';
import { selectDefaultFormula } from '../../../../store/reducers/formula';
import routes from '../../../routes.global';
import viewNames from '../../../../store/actions/view.types';
import { loadToolDescriptions } from '../../../../store/reducers/toolDescriptions';

// ------------------------------------
// Selectors
// ------------------------------------
export const STORE_KEY = 'predictions';
export const selectPredictionsState = state => state[STORE_KEY];

// ------------------------------------
// Reducer
// ------------------------------------
const actions = {};
const initialState = {
  matchups: [],
  teams: {},
  year: null,
  week: null,
  showProjections: false,
  formulaId: null,
  hasLoaded: false,
  error: null,
};

const predictionsReducer = (state = initialState, action) => {
  const handler = actions[action.type];

  return handler ? handler(state, action) : state;
};

export default predictionsReducer;

// ------------------------------------
// Actions
// ------------------------------------
export const LOAD_PREDICTIONS_REQUEST = `${STORE_KEY}/LOAD_PREDICTIONS_REQUEST`;
export const LOAD_PREDICTIONS_SUCCESS = `${STORE_KEY}/LOAD_PREDICTIONS_SUCCESS`;
export const LOAD_PREDICTIONS_ERROR = `${STORE_KEY}/LOAD_PREDICTIONS_ERROR`;

export const loadPredictionsRequest = ({ year, week, formulaId, showProjections }) => ({
  type: LOAD_PREDICTIONS_REQUEST,
  payload: { year, week, formulaId, showProjections },
});
export const loadPredictionsSuccess = (matchups, ranking) => ({
  type: LOAD_PREDICTIONS_SUCCESS,
  payload: { matchups, ranking },
});
export const loadPredictionsError = error => ({
  type: LOAD_PREDICTIONS_ERROR,
  payload: { error },
});

actions[LOAD_PREDICTIONS_REQUEST] = (
  state,
  { payload: { year, week, formulaId, showProjections } },
) => ({ ...state,
  year,
  week,
  formulaId,
  showProjections,
});

actions[LOAD_PREDICTIONS_SUCCESS] = (
  state,
  { payload: { matchups, ranking } },
) => ({ ...state,
  matchups,
  teams: groupBy(ranking, 'team_id'),
  hasLoaded: true,
});

actions[LOAD_PREDICTIONS_ERROR] = (state, { payload }) => ({ ...state,
  error: payload.error,
});

export const loadPredictions = (
  { year, week, formulaId, showProjections },
) => async (dispatch, getState, { api }) => {
  try {
    dispatch(beginTask());
    const state = getState();
    const { year: loadedYear, formulaId: loadedFormulaId, week: loadedWeek } = selectPredictionsState(state);
    dispatch(loadPredictionsRequest({ year, week, formulaId, showProjections }));
    if (year !== loadedYear || week !== loadedWeek || formulaId !== loadedFormulaId) {
      const position = selectPositions(getState(), 'projections').join(',');
      const [{ matchups }, { ranking }] = await Promise.all([
        api.projections.getPredictions({ year, week, formulaId }),
        api.projections.getPlayerProjections({ year, week, formulaId, position }),
      ]);
      dispatch(loadPredictionsSuccess(matchups, ranking));
    }
  } catch (error) {
    console.error(error);
    dispatch(loadPredictionsError(error));
  } finally {
    dispatch(endTask());
  }
};

// ------------------------------------
// View / Route
// ------------------------------------
export const PREDICTIONS_VIEW = `${STORE_KEY}/PREDICTIONS_VIEW`;
viewNames[PREDICTIONS_VIEW] = 'Predictions';
routes[PREDICTIONS_VIEW] = {
  path: '/in-season/nfl-fantasy-football-game-predictions',
  thunk: async (dispatch, getState) => {
    const state = getState();
    const { year: loadedYear, formulaId: loadedFormulaId, week: loadedWeek, showProjections: loadedShowProjections} = selectPredictionsState(state);
    let { year, formulaId, week, showProjections } = selectQuery(state);
    const { season: currentYear, week: currentWeek } = selectCurrent(state, 'predictions');
    const mediaType = state.browser.mediaType;

    // need to check strictly for typeof showProjections, but leaving in
    // Colby's logic commented out for the time being
    // showProjections = !!(showProjections === 'true');
    if (typeof showProjections === 'undefined') {
      mediaType === 'desktop' ?
        showProjections = loadedShowProjections ? loadedShowProjections : true :
        showProjections = loadedShowProjections ? loadedShowProjections : false;
    } else {
      console.log(showProjections, typeof showProjections === 'boolean' ? showProjections : showProjections === 'true');
      showProjections = typeof showProjections === 'boolean' ? showProjections : showProjections === 'true';
    }

    if (!formulaId) formulaId = loadedFormulaId || selectDefaultFormula(state).id;
    if (year === null || year === undefined || year === 'null') year = loadedYear || currentYear;
    await dispatch(loadWeeks(year));

    // if there's no week, let's default to the most recent week available for the given year
    if (typeof week !== 'number') {
      week = selectWeeks(getState(), year, 'predictions')[0];
    }

    // Needs to await otherwise data won't load on SSR
    await dispatch(loadPredictions({ year, week, formulaId, showProjections }));
    await dispatch(loadToolDescriptions());
    // Hard coding year and week until KKoston updates database
    // await dispatch(loadPredictions({ year: 2016, week: 6, formulaId, showProjections }));
  },
};



// WEBPACK FOOTER //
// ./src/app/views/Predictions/module/predictions.js